import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import quantinoAwake from '../../assets/avatars/quantino_liegend_1.png';
import quantinoAsleep from '../../assets/avatars/quantino_liegend_2.png';
import quantinoStreng from '../../assets/avatars/quantino_streng.png';
import quantinoTraurig from '../../assets/avatars/quantino_traurig.png';
import quantinoUeberrascht from '../../assets/avatars/quantino_überrascht.png';
import './styles.css';
import { createGamificationService } from '../../services/GamificationService';

const LyingQuantino = ({ user, accessToken, userId }) => {
    const [isAsleep, setIsAsleep] = useState(false);
    const [message, setMessage] = useState('');
    const [isMessageVisible, setIsMessageVisible] = useState(false);
    const [displayedMessage, setDisplayedMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [gamificationData, setGamificationData] = useState(null);
    const [clickReaction, setClickReaction] = useState(null); // 'streng', 'traurig', 'überrascht'
    const [isInteracting, setIsInteracting] = useState(false); // Prevent multiple interactions
    const [messageTimeout, setMessageTimeout] = useState(null);

    useEffect(() => {
        const fetchGamificationData = async () => {
            if (userId && accessToken) {
                try {
                    const gamificationService = createGamificationService(accessToken);
                    const data = await gamificationService.getGamificationSummary(userId);
                    setGamificationData(data);
                } catch (error) {
                    console.error("Failed to fetch gamification data for LyingQuantino", error);
                }
            }
        };
        fetchGamificationData();
    }, [userId, accessToken]);

    // Cleanup function for timeouts
    useEffect(() => {
        return () => {
            if (messageTimeout) {
                clearTimeout(messageTimeout);
            }
        };
    }, [messageTimeout]);

    // Typewriter effect
    useEffect(() => {
        if (!message || !isMessageVisible) {
            setDisplayedMessage('');
            setIsTyping(false);
            return;
        }

        setIsTyping(true);
        setDisplayedMessage('');

        let i = 0;
        const typingInterval = setInterval(() => {
            if (i < message.length) {
                setDisplayedMessage(message.substring(0, i + 1));
                i++;
            } else {
                clearInterval(typingInterval);
                setIsTyping(false);
            }
        }, 30); // Typing speed

        return () => {
            clearInterval(typingInterval);
        };
    }, [message, isMessageVisible]);

    useEffect(() => {
        if (!user || !gamificationData) return;

        const username = user?.username || user?.name || 'Student';

        const awakeMessages = [
            `Hey ${username}! Ready to learn?`,
            `Great to see you!`,
            `You've got ${gamificationData?.stats?.total_coins || 0} coins!`,
            `Your streak: ${gamificationData?.stats?.streak_days || 0} days!`,
            `Level ${gamificationData?.stats?.current_level || 1} looking good!`,
            `Let's tackle some questions!`,
            `Time to earn XP!`,
            `Knowledge is power!`,
            `You're doing amazing!`,
            `Keep up the momentum!`,
            `Learning mode: ON!`,
            `Brain training time!`,
            `Curiosity activated!`,
            `Ready for adventure?`,
            `Let's explore together!`,
            `Stop looking at me like that!`,
            `Why are you staring? 👀`,
            `I'm not a zoo animal!`,
            `Do I have something on my face?`,
            `Quit staring and start learning!`,
            `Eyes on the screen, not on me!`,
        ];

        const asleepMessages = [
            "Zzz... e = mc² ...",
            "Dreaming of data...",
            "Zzz... π = 3.14159...",
            "Sleeping on clouds...",
            "Zzz... algorithms...",
            "Counting sheep...",
            "Dream mode ON...",
            "Zzz... binary code...",
            "Snoozing peacefully...",
            "Zzz... knowledge...",
            "Sleeping deeply...",
            "Zzz... infinity...",
            "Zzz... stop watching me sleep...",
            "Zzz... dreaming of not being stared at...",
            "Zzz... privacy please...",
            "Zzz... even robots need rest...",
        ];

        const showNewMessage = () => {
            // Don't show new message if user is currently interacting
            if (isInteracting) return;
            
            const randomState = Math.random() < 0.5;
            setIsAsleep(randomState);

            if (randomState) {
                setMessage(asleepMessages[Math.floor(Math.random() * asleepMessages.length)]);
            } else {
                setMessage(awakeMessages[Math.floor(Math.random() * awakeMessages.length)]);
            }
            setIsMessageVisible(true);

            // Clear any existing timeout
            if (messageTimeout) {
                clearTimeout(messageTimeout);
            }

            const timeout = setTimeout(() => {
                setIsMessageVisible(false);
                setMessageTimeout(null);
            }, 5000); // Message visible for 5 seconds
            
            setMessageTimeout(timeout);
        };

        showNewMessage(); // Show first message immediately
        const intervalId = setInterval(showNewMessage, 20000); // New message every 20 seconds

        return () => clearInterval(intervalId); // Cleanup on unmount
    }, [user, gamificationData]);

    const handleQuantinoClick = () => {
        // Prevent multiple rapid clicks
        if (isInteracting) return;
        
        setIsInteracting(true);
        
        // Clear any existing timeout
        if (messageTimeout) {
            clearTimeout(messageTimeout);
            setMessageTimeout(null);
        }

        if (isAsleep) {
            // Wake up Quantino
            setIsAsleep(false);
            setClickReaction(null);
            
            const wakeUpMessages = [
                "Huh? What's happening?",
                "Did someone wake me up?",
                "I was having such a nice dream...",
                "Why did you wake me?!",
                "Five more minutes please...",
                "Was that really necessary?",
            ];
            
            setMessage(wakeUpMessages[Math.floor(Math.random() * wakeUpMessages.length)]);
            setIsMessageVisible(true);
            
            const timeout = setTimeout(() => {
                setIsMessageVisible(false);
                setClickReaction(null);
                setIsInteracting(false);
                setMessageTimeout(null);
            }, 4000);
            setMessageTimeout(timeout);
        } else {
            // Show annoyed reaction
            const reactions = ['streng', 'traurig', 'überrascht'];
            const selectedReaction = reactions[Math.floor(Math.random() * reactions.length)];
            setClickReaction(selectedReaction);
            
            const annoyedMessages = {
                streng: ["Ouch! Stop poking me!", "Leave me alone!", "I'm trying to help you learn!", "That hurts!"],
                traurig: ["Why are you bothering me?", "That makes me sad...", "I just want to help...", "Please be nice to me..."],
                überrascht: ["Whoa! What was that for?", "You surprised me!", "I wasn't expecting that!", "That was unexpected!"]
            };
            
            const messages = annoyedMessages[selectedReaction];
            setMessage(messages[Math.floor(Math.random() * messages.length)]);
            setIsMessageVisible(true);
            
            const timeout = setTimeout(() => {
                setIsMessageVisible(false);
                setClickReaction(null);
                setIsInteracting(false);
                setMessageTimeout(null);
            }, 3000);
            setMessageTimeout(timeout);
        }
    };

    const quantinoImage = clickReaction ? 
        (clickReaction === 'streng' ? quantinoStreng : 
         clickReaction === 'traurig' ? quantinoTraurig : 
         clickReaction === 'überrascht' ? quantinoUeberrascht : quantinoAwake) :
        (isAsleep ? quantinoAsleep : quantinoAwake);
    const bubbleClass = isAsleep ? 'thought-bubble' : 'speech-bubble';
    const bubbleClassWithVisibility = `${bubbleClass} ${isMessageVisible ? 'visible' : ''}`;

    return (
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', height: '120px' }}>
            <img 
                src={quantinoImage} 
                alt="Liegender Quantino" 
                style={{ 
                    height: '120px', 
                    zIndex: 10, 
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                onClick={handleQuantinoClick}
            />
            <Box className={bubbleClassWithVisibility}>
                <Typography variant="body2" sx={{ fontStyle: isAsleep ? 'italic' : 'normal' }}>
                    {displayedMessage}
                    {isTyping && <span className="typing-cursor"></span>}
                </Typography>
            </Box>
        </Box>
    );
};

export default LyingQuantino;
